//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getHasMultiHandleInstagramEnabled } from '@account-settings/redux/facebook-instagram/selectors/getMultiHandleFeature';
import {
  ContentLibrarySourcesGroups,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import HR from '@components/atoms/hr/HR';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import { MediaLibrarySourcesDropdownItemData } from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownConstants';
import {
  mapAllSources,
  mapBazaarvoiceSources,
  mapDropdownStateToSourcesOptions,
  mapFacebookSources,
  mapInstagramSources,
  mapPinterestSources,
  mapTikTokSources,
  mapYouTubeSources,
} from '@media-library/components/top-bar/sources-dropdown/mediaLibrarySourcesDropdownUtils';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import {
  useGetInstagramConnectedAccountsQuery,
  useGetInstagramPrimaryAccountQuery,
  useGetInstagramTrackedCompetitorsQuery,
  useGetInstagramTrackedUsersAndHashtagsQuery,
  useGetPinterestUserQuery,
  useGetTeamMembersQuery,
  useGetTikTokConnectionStatusQuery,
} from '@media-library/queries';
import { handleChangeSelectParent } from '@media-library/utils/sourcesUtils';
import { getTikTokMentionsFeatureEnabled } from '@publishing/analytics/selectors/getTikTokMentionsFeaturesEnabled';
import { getClientId } from '@redux/selectors/getClientId';
import { getIsDamImportDropboxEnabled } from '@redux/selectors/getIsDamImportDropboxEnabled';
import { getIsInstagramCompetitorsEnabled } from '@redux/selectors/getIsInstagramCompetitorsEnabled';
import { getClientName, getUserId } from '@redux/selectors/getSessionInfo';
import { isDefined } from '@utils/filterPredicates';

const getSocialPlatformTrackingId = (platform: ContentLibrarySourcesGroups) => {
  switch (platform) {
    case ContentLibrarySourcesGroups.ALL_INSTAGRAM:
      return MediaLibraryTrackingIds.PLATFORM_INSTAGRAM_CHECKBOX;
    case ContentLibrarySourcesGroups.ALL_FACEBOOK:
      return MediaLibraryTrackingIds.PLATFORM_FACEBOOK_CHECKBOX;
    case ContentLibrarySourcesGroups.ALL_TIKTOK:
      return MediaLibraryTrackingIds.PLATFORM_TIKTOK_CHECKBOX;
    case ContentLibrarySourcesGroups.ALL_PINTEREST:
      return MediaLibraryTrackingIds.PLATFORM_PINTEREST_CHECKBOX;
    case ContentLibrarySourcesGroups.ALL_YOUTUBE:
      return MediaLibraryTrackingIds.PLATFORM_YOUTUBE_CHECKBOX;
    case ContentLibrarySourcesGroups.ALL_BAZAARVOICE:
      return MediaLibraryTrackingIds.PLATFORM_BAZAARVOICE_CHECKBOX;
    default:
      return undefined;
  }
};

export interface SocialPlatformFilterProps {
  handleChangeSources: (sources: IContentLibrarySourcesOptions) => void;
  isInitiallyCollapsed?: boolean;
  selectedSources: IContentLibrarySourcesOptions;
}

export const SocialPlatformFilter = ({
  handleChangeSources,
  isInitiallyCollapsed,
  selectedSources,
}: SocialPlatformFilterProps) => {
  const clientId = useSelector(getClientId);
  const clientName = useSelector(getClientName);
  const currentUserId = useSelector(getUserId);

  const { data: teamMembers = [] } = useGetTeamMembersQuery(
    clientId,
    clientName,
  );

  const { data: defaultInstagramAccount } =
    useGetInstagramPrimaryAccountQuery();

  const { data: competitors = [] } = useGetInstagramTrackedCompetitorsQuery();

  const { data: instagramTrackedUsersAndHashtags } =
    useGetInstagramTrackedUsersAndHashtagsQuery();
  const {
    businessUsers = [],
    hashtags = [],
    users: regularUsers = [],
  } = instagramTrackedUsersAndHashtags ?? {};

  const { data: pinterestUser } = useGetPinterestUserQuery();

  const { data: defaultTikTokAccount } = useGetTikTokConnectionStatusQuery();

  const { data: connectedInstagramAccounts } =
    useGetInstagramConnectedAccountsQuery();

  const isInstagramCompetitorsEnabled = useSelector(
    getIsInstagramCompetitorsEnabled,
  );
  const isDamImportDropboxEnabled = useSelector(getIsDamImportDropboxEnabled);
  const isMultiHandleFeatureEnabled = useSelector(
    getHasMultiHandleInstagramEnabled,
  );

  const isTikTokMentionsEnabled = useSelector(getTikTokMentionsFeatureEnabled);

  const tiktokConfigs = { defaultTikTokAccount };
  const instagramConfigs = {
    connectedInstagramAccounts: connectedInstagramAccounts,
    defaultInstagramAccount,
    trackedBusinessUsers: businessUsers,
    trackedCompetitors: competitors,
    trackedHashtags: hashtags,
    trackedUsers: regularUsers,
  };

  const featureFlags = {
    isDamImportDropboxEnabled,
    isInstagramCompetitorsEnabled,
    isMultiHandleFeatureEnabled,
    isTikTokMentionsEnabled,
  };

  const socialPlatforms = [
    mapInstagramSources({ featureFlags, instagramConfigs, selectedSources }),
    mapFacebookSources(selectedSources),
    mapTikTokSources({ featureFlags, selectedSources, tiktokConfigs }),
    pinterestUser
      ? mapPinterestSources(pinterestUser, selectedSources)
      : undefined,
    mapYouTubeSources(selectedSources),
    mapBazaarvoiceSources(selectedSources),
  ].filter(isDefined);

  const allSources = mapAllSources({
    currentUserId,
    featureFlags,
    selectedSources,
    sourcesConfigs: {
      instagramConfigs,
      pinterestUser,
      teamMembers,
      tiktokConfigs,
    },
  });

  const createUpdatedItem = (
    item: MediaLibrarySourcesDropdownItemData,
    updatedSelectedValue: boolean,
  ): MediaLibrarySourcesDropdownItemData => {
    return {
      ...item,
      isIndeterminate: updatedSelectedValue,
      isSelected: updatedSelectedValue,
      subItems:
        item.subItems?.map((subItem) =>
          createUpdatedItem(subItem, updatedSelectedValue),
        ) ?? [],
    };
  };

  const handleChange = (
    item: MediaLibrarySourcesDropdownItemData | undefined,
  ) => {
    if (item) {
      const updatedSelectedValue = !item.isSelected;
      const updatedItem = createUpdatedItem(item, updatedSelectedValue);
      const updatedSources = handleChangeSelectParent(allSources, updatedItem);
      const currentSourcesOptions =
        mapDropdownStateToSourcesOptions(updatedSources);

      handleChangeSources(currentSourcesOptions);
    }
  };

  return (
    <>
      <CollapsibleSection isCollapsed={isInitiallyCollapsed} title='Platform'>
        <CheckboxSet>
          {socialPlatforms.length > 0 &&
            socialPlatforms.map((option) => (
              <Checkbox
                key={option.label}
                handleClick={() => {
                  handleChange(option);
                }}
                isChecked={option.isIndeterminate}
                label={option.label}
                trackingId={getSocialPlatformTrackingId(option.value)}
              />
            ))}
        </CheckboxSet>
      </CollapsibleSection>
      <HR />
    </>
  );
};

const CheckboxSet = styled.div`
  display: flex;
  flex-direction: column;
`;

const Checkbox = styled(LabeledCheckbox)(
  ({ theme }) => `
    height: unset;
    padding: ${theme.spacings[2]} 0;
  `,
);
